import React, { useMemo } from 'react';
import { Printer, X, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTenant } from '../contexts/TenantContext';
import RightDrawer from './RightDrawer';
import PharmacyAuditTrail from './PharmacyAuditTrail';

/**
 * Branded pharmacy receipt with India-GST breakdown (CGST + SGST split).
 * Reads tenant branding so the printed slip carries the clinic's logo,
 * tagline, address and footer disclaimer — same visual language as
 * PaymentReceipt so the patient sees a consistent identity across
 * consultation + pharmacy.
 */
export default function PharmacyReceipt({ data, onClose }) {
  const { tenant } = useTenant();
  const b = tenant?.branding || {};
  const name = b.display_name || tenant?.name || 'Pharmacy';
  const primary = b.primary_color || '#0f766e';
  const logo = b.prescription_logo_url || b.logo_url;
  const lhLines = (b.letterhead_text || '').split('\n').map(s => s.trim()).filter(Boolean);
  const tagline = lhLines[0] || b.tagline || '';
  const addressLines = lhLines.slice(1);
  const footer = b.prescription_footer_text || '';
  const now = new Date();
  const stamp = now.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' });
  const receiptNo = data?.receipt_no || `PH-${Date.now().toString().slice(-8)}`;
  const verifyCode = useMemo(() => (receiptNo || '').replace(/[^A-Z0-9]/gi, '').slice(-8).toUpperCase(), [receiptNo]);

  const tint = (hex, alpha) => {
    const h = (hex || '').replace('#', '');
    if (h.length !== 6) return `rgba(15, 118, 110, ${alpha})`;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const bl = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${bl}, ${alpha})`;
  };

  // body scroll lock handled by RightDrawer

  const items = data?.items || [];
  const subtotal = Number(data?.subtotal || 0);
  const cgst = Number(data?.cgst || 0);
  const sgst = Number(data?.sgst || 0);
  const total = Number(data?.amount || data?.grand_total || subtotal + cgst + sgst);

  return (
    <RightDrawer
      open={true}
      onClose={onClose}
      title={`Pharmacy Tax Invoice · ${name}`}
      subtitle={`${data?.patient_name || '—'} · ₹${total.toFixed(2)} · ${(data?.payment_method || 'cash').toUpperCase()}`}
      icon={<ShieldCheck className="h-5 w-5 text-white" />}
      accent={primary}
      widthClass="w-full md:w-[44vw] max-w-[640px]"
      printableId="print-rx-receipt"
      data-testid="pharmacy-receipt-modal"
      footer={
        <>
          <Button variant="outline" onClick={onClose} data-testid="rx-receipt-close">
            <X className="h-3.5 w-3.5 mr-1" /> Close
          </Button>
          <Button onClick={() => window.print()} className="text-white" style={{ background: primary }} data-testid="rx-receipt-print">
            <Printer className="h-3.5 w-3.5 mr-1" /> Print invoice
          </Button>
        </>
      }
    >
      <style>{`
        @media print {
          @page { margin: 12mm; }
          body * { visibility: hidden; }
          #print-rx-receipt, #print-rx-receipt * { visibility: visible; }
          #print-rx-receipt { position: absolute; left: 0; top: 0; width: 100%; }
          #print-rx-receipt .rd-no-print,
          #print-rx-receipt [data-print-hidden] {
            display: none !important;
            visibility: hidden !important;
          }
        }
      `}</style>
      <div className="p-6">
      <div className="bg-white rounded-2xl shadow-md max-w-lg mx-auto overflow-hidden ring-1 ring-zinc-100 print:shadow-none print:max-w-full print:rounded-none">
        <div className="receipt-card-inner">
          {/* HEADER */}
          <div className="relative text-white overflow-hidden"
               style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 55%, ${primary}99 100%)` }}>
            <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
                 style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '14px 14px' }} />
            <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full border-[12px] border-white/10" />
            <div className="relative px-7 pt-6 pb-5">
              <div className="flex items-center gap-3">
                {logo ? (
                  <img src={logo} alt="logo" className="h-14 w-14 rounded-xl bg-white p-1.5 object-contain shadow-md ring-2 ring-white/60" />
                ) : (
                  <div className="h-14 w-14 rounded-xl bg-white/95 flex items-center justify-center font-extrabold text-2xl shadow-md ring-2 ring-white/60"
                       style={{ color: primary, fontFamily: 'Manrope' }}>
                    {name[0]?.toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-extrabold text-[20px] leading-tight tracking-tight truncate" style={{ fontFamily: 'Manrope' }}>{name}</p>
                  {tagline && <p className="text-[11px] uppercase tracking-[0.18em] opacity-90 truncate mt-0.5">{tagline}</p>}
                </div>
              </div>
              <div className="mt-5 mb-3 flex items-center gap-3">
                <div className="h-px flex-1 bg-white/30" />
                <p className="text-[10px] uppercase tracking-[0.35em] font-semibold opacity-95">Pharmacy Tax Invoice</p>
                <div className="h-px flex-1 bg-white/30" />
              </div>
              <div className="flex items-end justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-widest opacity-80">Grand Total (incl. GST)</p>
                  <p className="text-3xl font-extrabold tracking-tight leading-none mt-0.5" style={{ fontFamily: 'Manrope' }}>
                    ₹{total.toFixed(2)}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[10px] uppercase tracking-widest opacity-80">Invoice #</p>
                  <p className="font-mono text-[12px] font-semibold mt-0.5 bg-white/15 px-2 py-1 rounded-md border border-white/20">{receiptNo}</p>
                </div>
              </div>
            </div>
            <div className="relative h-3 -mb-px">
              <div className="absolute inset-x-3 top-1/2 -translate-y-1/2 flex items-center justify-between">
                {Array.from({ length: 22 }).map((_, i) => (<span key={i} className="block h-1.5 w-1.5 rounded-full bg-white/95" />))}
              </div>
            </div>
          </div>

          {/* META */}
          <div className="px-7 pt-4 pb-2 space-y-1.5"
               style={{ background: `linear-gradient(180deg, ${tint(primary, 0.04)} 0%, white 60%)` }}>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500 text-[11px] uppercase tracking-wider">Date & time</span>
              <span className="font-semibold text-zinc-800">{stamp}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500 text-[11px] uppercase tracking-wider">Patient</span>
              <span className="font-bold text-zinc-900">{data?.patient_name || '—'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500 text-[11px] uppercase tracking-wider">Payment</span>
              <span className="font-semibold text-zinc-800">
                {(data?.payment_method || 'cash').toUpperCase()}
                {data?.payment_reference ? ` · ${data.payment_reference}` : ''}
              </span>
            </div>
          </div>

          {/* ITEMS TABLE */}
          <div className="px-7 pb-3">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-zinc-500 text-[10px] uppercase tracking-wider border-b"
                    style={{ borderColor: tint(primary, 0.25) }}>
                  <th className="text-left py-1.5">Item</th>
                  <th className="text-right py-1.5">Qty</th>
                  <th className="text-right py-1.5">Rate</th>
                  <th className="text-right py-1.5">GST%</th>
                  <th className="text-right py-1.5">Line</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const qty = Number(it.qty || 0);
                  const rate = Number(it.price || it.mrp || 0);
                  return (
                    <tr key={i} className="border-b border-zinc-100">
                      <td className="py-1.5 truncate max-w-[180px]">{it.name}</td>
                      <td className="py-1.5 text-right">{qty}</td>
                      <td className="py-1.5 text-right">₹{rate.toFixed(2)}</td>
                      <td className="py-1.5 text-right">{(it.gst_percent ?? 12)}%</td>
                      <td className="py-1.5 text-right font-semibold">₹{(qty * rate).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* TOTALS */}
          <div className="px-7 pb-3">
            <div className="space-y-1 text-sm" style={{ color: primary }}>
              <Row label="Subtotal"  value={`₹${subtotal.toFixed(2)}`} />
              <Row label="CGST"      value={`₹${cgst.toFixed(2)}`} />
              <Row label="SGST"      value={`₹${sgst.toFixed(2)}`} />
              <div className="border-t pt-1.5 mt-1" style={{ borderColor: tint(primary, 0.3) }}>
                <div className="flex justify-between items-center font-extrabold text-base">
                  <span>Grand Total</span><span>₹{total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* AUDIT TRAIL — only on-screen; suppressed from the print invoice */}
          {(data?.audit_log || []).length > 0 && (
            <div className="px-7 pb-3 rd-no-print" data-print-hidden>
              <PharmacyAuditTrail entries={data.audit_log} />
            </div>
          )}

          {/* FOOTER */}
          <div className="relative">
            <div className="relative h-3 -mt-px" aria-hidden>
              <div className="absolute inset-x-3 top-1/2 -translate-y-1/2 flex items-center justify-between">
                {Array.from({ length: 22 }).map((_, i) => (<span key={i} className="block h-1.5 w-1.5 rounded-full" style={{ background: tint(primary, 0.18) }} />))}
              </div>
            </div>
            <div className="px-7 pt-3 pb-5" style={{ background: tint(primary, 0.04) }}>
              <div className="grid grid-cols-5 gap-4 items-end mb-4">
                <div className="col-span-3">
                  <div className="border-b border-zinc-400/50 h-7" />
                  <p className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-1">Authorised signature</p>
                </div>
                <div className="col-span-2 text-right">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border-2 border-dashed"
                       style={{ borderColor: tint(primary, 0.4), color: primary }}>
                    <ShieldCheck className="h-3.5 w-3.5" />
                    <span className="text-[10px] font-bold tracking-wider">VERIFIED</span>
                  </div>
                  <p className="font-mono text-[9px] text-zinc-500 mt-1 tracking-wider">#{verifyCode}</p>
                </div>
              </div>
              {(addressLines.length > 0 || footer) && (
                <div className="border-t pt-3" style={{ borderColor: tint(primary, 0.2) }}>
                  {addressLines.length > 0 && (
                    <p className="text-[11px] text-zinc-700 leading-relaxed font-medium text-center" style={{ fontFamily: 'Manrope' }}>
                      {addressLines.join(' · ')}
                    </p>
                  )}
                  {footer && (
                    <p className="text-[10px] text-zinc-500 italic mt-2 whitespace-pre-wrap text-center leading-relaxed">{footer}</p>
                  )}
                </div>
              )}
              <p className="text-[9px] text-zinc-400 text-center mt-3 leading-relaxed">
                This is a computer-generated tax invoice and is valid without a physical signature. Please retain it for your records. Quote invoice <span className="font-mono">{receiptNo}</span> for any queries.
              </p>
            </div>
          </div>
        </div>
      </div>
      </div>
    </RightDrawer>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-zinc-600 text-xs uppercase tracking-wider">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
